/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import { getErrorFromEndpoint, isStrEmptyOrNull } from '../../common/UtilityFunctions';

/***
 * Get all active batches
 */
const useGetBatchList = () => {
  const [batches, setBatches] = useState<Array<ServicingBatch>>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { token } = useAuthContext();

  const { get } = useAbortController();

  const getBatchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const url = process.env.REACT_APP_BATCH_LIST;

    const controller = get();
    const config = { signal: controller.signal };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    try {
      const response = await axiosCall({ url: url, method: 'get' }, config, headers);
      const err = getErrorFromEndpoint(response);
      setErrorMessage(err);
      if (!isStrEmptyOrNull(err)) {
        setBatches([]);
      } else {
        setBatches(response);
      }
      setLoading(false);
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') {
        setBatches([]);
        setErrorMessage('Error Loading Batches.');
        setLoading(false);
        console.error(error.message);
      }
    }
  }, [get, token]);

  useEffect(() => {
    getBatchList();
  }, [getBatchList]);

  // Judith Espinal - 04/01/2026 - SQC-590: Only include active batches with blank postedSource or MANUAL keyword, and under 20M posted
  const filteredBatches = useMemo(
    () =>
      batches.filter(
        (batch) =>
          batch.active &&
          (isStrEmptyOrNull(batch.postedSource) || batch.postedSourceKeyword === 'MANUAL') &&
          batch.amountPosted < 20000000,
      ),
    [batches],
  );

  return {
    batches: batches,
    filteredBatches: filteredBatches,
    batchesErrorMessages: errorMessage,
    loadingBatches: loading,
  };
};

export default useGetBatchList;
